import { createContext, useCallback, useRef } from "react";
import {
  IEventModal,
  IMetaRef,
  IModalContextProviderProps,
  IModalFunctionOptions,
  IModalReturn,
  ModalEventType
} from "./models/modal.types";
import "./modal.css";
import Dialog from "./Modal";

export const ModalContext = createContext<IModalReturn | undefined>(undefined);

export const ModalContextProvider: React.FC<IModalContextProviderProps> = ({
  children
}) => {
  const resolveRef =
    useRef<(value: IEventModal | PromiseLike<IEventModal>) => void>();
  const metaRef = useRef<IMetaRef | null>(null);
  const isOpenRef = useRef<boolean>(false);
  // Queue to handle multiple modal invocations without cancelling each other
  const queueRef = useRef<
    Array<{ meta: IMetaRef; resolve: (value: IEventModal | PromiseLike<IEventModal>) => void }>
  >([]);

  // Explicit setter to avoid toggle-related race conditions
  const setModalOpen = useCallback((open: boolean) => {
    isOpenRef.current = open;
    document.dispatchEvent(
      new CustomEvent("mx-modal-toggle", { detail: { isOpen: open } })
    );
  }, []);

  const updateRefResolver = useCallback(
    (key: keyof ModalEventType, value: unknown) => {
      const baseMeta: ModalEventType = {
        close: false,
        confirm: false,
        dismiss: false
      };

      baseMeta[key] = true;

      if (resolveRef.current) {
        resolveRef.current({
          type: baseMeta,
          value
        });
      }
      // Close current modal
      setModalOpen(false);
      // After close animation completes, clear and open next in queue if present
      setTimeout(() => {
        metaRef.current = null;
        const next = queueRef.current.shift();
        if (next) {
          metaRef.current = next.meta;
          resolveRef.current = next.resolve;
          setModalOpen(true);
        }
      }, 450);
    },
    [setModalOpen]
  );

  const onClose = useCallback(
    (value?: IEventModal["value"]) => {
      updateRefResolver("close", value);
    },
    [updateRefResolver]
  );

  const onDismiss = useCallback(
    (value?: IEventModal["value"]) => {
      updateRefResolver("dismiss", value);
    },
    [updateRefResolver]
  );

  const onConfirm = useCallback(
    (value?: IEventModal["value"]) => {
      updateRefResolver("confirm", value);
    },
    [updateRefResolver]
  );

  const getData = useCallback<() => unknown>(() => {
    return metaRef.current?.data;
  }, []);

  const getOptions = useCallback(() => {
    return metaRef.current?.options;
  }, []);

  const getTemplates = useCallback(() => {
    return metaRef.current?.template;
  }, [JSON.stringify(metaRef.current?.template)]);

  const modal = useCallback(
    (p: IModalFunctionOptions) => {
      return new Promise<IEventModal>(resolve => {
        // If not open, open immediately
        if (!isOpenRef.current && !metaRef.current) {
          metaRef.current = { ...p };
          resolveRef.current = resolve;
          setModalOpen(true);
        } else {
          // Otherwise enqueue for later
          queueRef.current.push({ meta: { ...p }, resolve });
        }
      });
    },
    [setModalOpen]
  );

  const contextValue = {
    modal,
    onClose,
    onDismiss,
    onConfirm,
    getData,
    getOptions,
    metaRef,
    isOpenRef,
    getTemplates
  };

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      <Dialog />
    </ModalContext.Provider>
  );
};
